<template>
  <div>
    <b-card>
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <div>
          <h5 class="mb-0">
            Logins
            <b-badge
              :variant="lockStatus.is_locked ? 'danger' : 'success'"
              class="ml-1"
            >
              {{ lockStatus.is_locked ? 'Locked' : 'Open' }}
            </b-badge>
          </h5>
          <small
            v-if="lockStatus.is_locked"
            class="text-muted"
          >
            Locked by {{ lockStatus.admin && lockStatus.admin.name }}
            <span v-if="lockStatus.reason">— {{ lockStatus.reason }}</span>
            (admins can still log in)
          </small>
        </div>

        <div class="d-flex flex-wrap py-1">
          <b-button
            v-if="!lockStatus.is_locked"
            variant="outline-warning"
            class="mr-1"
            :disabled="lockLoading"
            @click="promptLock"
          >
            Lock new logins
          </b-button>
          <b-button
            v-else
            variant="outline-success"
            class="mr-1"
            :disabled="lockLoading"
            @click="unlock"
          >
            Unlock logins
          </b-button>

          <b-button
            variant="danger"
            :disabled="revokeAllLoading"
            @click="promptRevokeAll"
          >
            Force logout everyone
          </b-button>
        </div>
      </div>
    </b-card>

    <b-card v-if="selectedIds.length">
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <span>{{ selectedIds.length }} selected</span>
        <div class="d-flex flex-wrap py-1">
          <b-button
            variant="outline-warning"
            class="mr-1"
            :disabled="bulkLoading"
            @click="promptRevokeSelected"
          >
            Revoke selected
          </b-button>
          <b-button
            variant="outline-danger"
            :disabled="bulkLoading"
            @click="promptDeleteSelected"
          >
            Delete selected
          </b-button>
        </div>
      </div>
    </b-card>

    <b-card>
      <b-row>
        <b-col
          cols="12"
          md="4"
          class="py-1"
        >
          <b-form-input
            v-model="filters.search"
            placeholder="Search name or email"
            @keyup.enter="applyFilters"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="py-1"
        >
          <b-form-select
            v-model="filters.role"
            :options="roleOptions"
            @change="applyFilters"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="py-1"
        >
          <b-form-select
            v-model="filters.country"
            :options="countryOptions"
            @change="applyFilters"
          />
        </b-col>
        <b-col
          cols="6"
          md="2"
          class="py-1"
        >
          <b-form-select
            v-model="filters.status"
            :options="statusOptions"
            @change="applyFilters"
          />
        </b-col>
        <b-col
          cols="6"
          md="2"
          class="py-1"
        >
          <b-form-select
            v-model="perPage"
            :options="[10, 20, 50, 100, 200, 500]"
            @change="onPerPageChange"
          />
        </b-col>
      </b-row>

      <div
        v-if="loading"
        class="text-center my-2"
      >
        <b-spinner
          type="grow"
          label="Loading..."
        />
      </div>

      <b-table
        v-else
        :items="sessions"
        :fields="columns"
        responsive
        show-empty
        bordered
        primary-key="id"
      >
        <template #head(select)>
          <b-form-checkbox
            :checked="allOnPageSelected"
            @change="toggleSelectAllOnPage"
          />
        </template>

        <template #cell(select)="data">
          <b-form-checkbox
            :checked="selectedIds.includes(data.item.id)"
            @change="toggleSelect(data.item.id)"
          />
        </template>

        <template #cell(name)="data">
          <div>
            <div class="font-weight-bold">
              {{ data.item.name }}
            </div>
            <small class="text-muted">{{ data.item.email }}</small>
          </div>
        </template>

        <template #cell(roles)="data">
          <b-badge
            v-for="role in data.item.roles"
            :key="role"
            variant="light-primary"
            class="mr-1"
          >
            {{ role }}
          </b-badge>
        </template>

        <template #cell(location)="data">
          <div>
            <div>{{ data.item.ip_address }}</div>
            <small class="text-muted">
              {{ [data.item.city, data.item.country].filter(Boolean).join(', ') }}
            </small>
          </div>
        </template>

        <template #cell(last_used_at)="data">
          {{ data.item.last_used_at ? new Date(data.item.last_used_at).toLocaleString() : '—' }}
        </template>

        <template #cell(status)="data">
          <b-badge :variant="data.item.status === 'revoked' ? 'secondary' : 'success'">
            {{ data.item.status }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <span
            v-if="data.item.status !== 'revoked'"
            title="Revoke session"
            class="mr-1"
          >
            <feather-icon
              icon="TrashIcon"
              class="cursor-pointer"
              @click="promptRevoke(data.item)"
            />
          </span>
          <span title="Delete permanently">
            <feather-icon
              icon="Trash2Icon"
              class="cursor-pointer text-danger"
              @click="promptDelete(data.item)"
            />
          </span>
        </template>
      </b-table>

      <Pagination
        :page="currentPage"
        :per-page="perPage"
        :total-rows="totalRows"
        @get-all-data="onPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Pagination from '@/components/general-table/specific-utils/Pagination.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  components: { Pagination },
  data() {
    return {
      apiUrl: 'sessions',
      lockStatus: { is_locked: false, admin: null, reason: null },
      lockLoading: false,
      revokeAllLoading: false,
      loading: false,
      sessions: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 20,
      filters: {
        search: '', role: '', country: '', status: 'active',
      },
      roles: [],
      countries: [],
      statusOptions: [
        { value: 'active', text: 'Active' },
        { value: 'revoked', text: 'Revoked' },
        { value: 'all', text: 'All' },
      ],
      selectedIds: [],
      bulkLoading: false,
      columns: [
        { key: 'select', label: '' },
        { key: 'name', label: 'User' },
        { key: 'roles', label: 'Roles' },
        { key: 'device', label: 'Device' },
        { key: 'location', label: 'IP / Location' },
        { key: 'last_used_at', label: 'Last active' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },
  computed: {
    roleOptions() {
      return [{ value: '', text: 'All roles' }, ...this.roles.map(r => ({ value: r, text: r }))]
    },
    countryOptions() {
      return [{ value: '', text: 'All countries' }, ...this.countries.map(c => ({ value: c, text: c }))]
    },
    allOnPageSelected() {
      return this.sessions.length > 0 && this.sessions.every(s => this.selectedIds.includes(s.id))
    },
  },
  mounted() {
    this.fetchLockStatus()
    this.fetchSessions()
  },
  methods: {
    notify(title, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'BellIcon', variant },
      })
    },
    errorMessage(error) {
      try {
        return decryptData(error.response.data.payload).message
      } catch (e) {
        return 'An unknown error occurred'
      }
    },
    applyFilters() {
      this.currentPage = 1
      this.fetchSessions()
    },
    onPerPageChange() {
      this.currentPage = 1
      this.fetchSessions()
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchSessions()
    },
    async fetchSessions() {
      this.loading = true
      try {
        const response = await axios.get(this.apiUrl, {
          params: {
            search: this.filters.search || undefined,
            role: this.filters.role || undefined,
            country: this.filters.country || undefined,
            status: this.filters.status,
            skip: (this.currentPage - 1) * this.perPage,
            limit: this.perPage,
          },
        })
        const { data } = decryptData(response.data.payload)
        this.sessions = data.sessions
        this.totalRows = data.count
        this.roles = data.roles
        this.countries = data.countries
        this.selectedIds = []
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      } finally {
        this.loading = false
      }
    },
    toggleSelect(id) {
      this.selectedIds = this.selectedIds.includes(id)
        ? this.selectedIds.filter(i => i !== id)
        : [...this.selectedIds, id]
    },
    toggleSelectAllOnPage() {
      this.selectedIds = this.allOnPageSelected ? [] : this.sessions.map(s => s.id)
    },
    async fetchLockStatus() {
      try {
        const response = await axios.get('sessions/lock-status')
        this.lockStatus = decryptData(response.data.payload).data
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      }
    },
    promptLock() {
      this.$swal({
        title: 'Lock new logins?',
        text: 'Staff will not be able to log in until you unlock this. Admins can still log in.',
        input: 'text',
        inputPlaceholder: 'Reason (optional)',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Lock logins',
      }).then(result => {
        if (result.isConfirmed) this.lock(result.value)
      })
    },
    async lock(reason) {
      this.lockLoading = true
      try {
        await axios.post('sessions/lock', { reason: reason || null })
        this.notify('New logins are now locked')
        this.fetchLockStatus()
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      } finally {
        this.lockLoading = false
      }
    },
    async unlock() {
      this.lockLoading = true
      try {
        await axios.post('sessions/unlock')
        this.notify('Logins are unlocked')
        this.fetchLockStatus()
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      } finally {
        this.lockLoading = false
      }
    },
    promptRevoke(session) {
      this.$swal({
        title: 'Revoke this session?',
        text: `${session.name || session.email} will be signed out immediately.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Revoke',
      }).then(result => {
        if (result.isConfirmed) this.revoke(session.id)
      })
    },
    async revoke(id) {
      try {
        await axios.post(`sessions/${id}/revoke`)
        this.notify('Session revoked')
        this.fetchSessions()
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      }
    },
    promptDelete(session) {
      this.$swal({
        title: 'Delete this session permanently?',
        text: `This removes ${session.name || session.email}'s session row entirely — it will no longer show up as revoked history either.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Delete',
      }).then(result => {
        if (result.isConfirmed) this.deleteOne(session.id)
      })
    },
    async deleteOne(id) {
      try {
        await axios.delete(`sessions/${id}`)
        this.notify('Session deleted')
        this.fetchSessions()
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      }
    },
    promptRevokeSelected() {
      this.$swal({
        title: `Revoke ${this.selectedIds.length} selected session(s)?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Revoke selected',
      }).then(result => {
        if (result.isConfirmed) this.revokeSelected()
      })
    },
    async revokeSelected() {
      this.bulkLoading = true
      try {
        const response = await axios.post('sessions/revoke-selected', { ids: this.selectedIds })
        const { revoked } = decryptData(response.data.payload).data
        this.notify(`Revoked ${revoked} session(s)`)
        this.fetchSessions()
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      } finally {
        this.bulkLoading = false
      }
    },
    promptDeleteSelected() {
      this.$swal({
        title: `Permanently delete ${this.selectedIds.length} selected session(s)?`,
        text: 'This removes the rows entirely — cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Delete selected',
      }).then(result => {
        if (result.isConfirmed) this.deleteSelected()
      })
    },
    async deleteSelected() {
      this.bulkLoading = true
      try {
        const response = await axios.post('sessions/delete-selected', { ids: this.selectedIds })
        const { deleted } = decryptData(response.data.payload).data
        this.notify(`Deleted ${deleted} session(s)`)
        this.fetchSessions()
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      } finally {
        this.bulkLoading = false
      }
    },
    promptRevokeAll() {
      this.$swal({
        title: 'Force logout everyone?',
        text: 'This immediately signs out every user and admin currently logged in, everywhere. Type "logout" to confirm.',
        icon: 'warning',
        input: 'text',
        inputPlaceholder: 'Type logout to confirm',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Log everyone out',
        inputValidator: value => (value === 'logout' ? null : 'Type "logout" (lowercase) to confirm'),
      }).then(result => {
        if (result.isConfirmed) this.revokeAll()
      })
    },
    async revokeAll() {
      this.revokeAllLoading = true
      try {
        const response = await axios.post('sessions/revoke-all')
        const { tokens_revoked: revoked } = decryptData(response.data.payload).data
        this.notify(`Logged everyone out (${revoked} sessions revoked)`)
        this.fetchSessions()
      } catch (error) {
        this.notify(this.errorMessage(error), 'danger')
      } finally {
        this.revokeAllLoading = false
      }
    },
  },
}
</script>
